import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Helper to resolve image URLs (exported for use in components)
export const resolveImageUrl = (url) => {
  if (!url) return null;
  // If URL is already absolute, return it
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Storage path (e.g. "uclean/feed/user123/abc.jpg")
  if (url.startsWith('uclean/')) {
    return `${BACKEND_URL}/api/files/${url}`;
  }
  // If URL is relative (starts with /api), prepend backend URL
  if (url.startsWith('/api')) {
    return `${BACKEND_URL}${url}`;
  }
  // Relative path without prefix - assume storage
  if (!url.startsWith('/')) {
    return `${BACKEND_URL}/api/files/${url}`;
  }
  return `${BACKEND_URL}${url}`;
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (token expired/invalid)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token inválido ou expirado
      localStorage.removeItem('token');
      // Redirecionar para login se não estiver na página de login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/me/profile'),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Professionals APIs
export const professionalsAPI = {
  // Profile
  createProfile: (data) => api.post('/professionals/me', data),
  getMyProfile: () => api.get('/professionals/me'),
  updateProfile: (data) => api.put('/professionals/me', data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/professionals/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  pauseProfile: () => api.post('/professionals/me/pause'),
  activateProfile: () => api.post('/professionals/me/activate'),
  
  // Marketplace
  list: (params) => {
    const queryParams = new URLSearchParams();
    if (params?.specialty) queryParams.append('specialty', params.specialty);
    if (params?.modality) queryParams.append('modality', params.modality);
    if (params?.city) queryParams.append('city', params.city);
    if (params?.state) queryParams.append('state', params.state);
    if (params?.price_min) queryParams.append('price_min', params.price_min);
    if (params?.price_max) queryParams.append('price_max', params.price_max);
    if (params?.q) queryParams.append('q', params.q);
    if (params?.cursor) queryParams.append('cursor', params.cursor);
    if (params?.limit) queryParams.append('limit', params.limit);
    return api.get(`/professionals?${queryParams}`);
  },
  getBySlug: (slug) => api.get(`/professionals/${slug}`),
  
  // Availability
  createAvailabilityRule: (data) => api.post('/professionals/me/availability/rules', data),
  getAvailabilityRules: () => api.get('/professionals/me/availability/rules'),
  deleteAvailabilityRule: (ruleId) => api.delete(`/professionals/me/availability/rules/${ruleId}`),
  createAvailabilityException: (data) => api.post('/professionals/me/availability/exceptions', data),
  getAvailabilityExceptions: () => api.get('/professionals/me/availability/exceptions'),
  deleteAvailabilityException: (exceptionId) => api.delete(`/professionals/me/availability/exceptions/${exceptionId}`),
  getSlots: (slug, from, to, tz = 'America/Sao_Paulo') => {
    const params = new URLSearchParams({ from, to, tz });
    return api.get(`/professionals/${slug}/slots?${params}`);
  },
  
  // Bookings
  book: (slug, data) => api.post(`/professionals/${slug}/book`, data),
  getMyBookings: (status) => {
    const params = status ? `?status=${status}` : '';
    return api.get(`/professionals/me/bookings${params}`);
  },
  cancelBooking: (bookingId) => api.post(`/professionals/bookings/${bookingId}/cancel`),
};

// Feed APIs
export const feedAPI = {
  getPosts: (cursor, limit = 20) => {
    const params = new URLSearchParams();
    if (cursor) params.append('cursor', cursor);
    params.append('limit', limit);
    return api.get(`/feed?${params}`);
  },
  createPost: (data) => api.post('/feed/posts', data),
  createPostWithImages: (formData) => api.post('/feed/posts-with-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadFeedImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/feed/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  likePost: (postId) => api.post(`/feed/${postId}/like`),
  unlikePost: (postId) => api.delete(`/feed/${postId}/like`),
  commentPost: (postId, content) => api.post(`/feed/${postId}/comment`, { content }),
  getComments: (postId) => api.get(`/feed/${postId}/comments`),
  reportPost: (postId, reason) => api.post(`/feed/${postId}/report`, { reason }),
  editPost: (postId, data) => api.put(`/feed/${postId}`, data),
  deletePost: (postId) => api.delete(`/feed/${postId}`),
  adminDeletePost: (postId) => api.delete(`/feed/admin/${postId}`),
};

// Groups APIs
export const groupsAPI = {
  getGroups: (params) => {
    const queryParams = new URLSearchParams();
    if (params?.q) queryParams.append('q', params.q);
    if (params?.category) params.category.forEach(c => queryParams.append('category', c));
    if (params?.sort) queryParams.append('sort', params.sort);
    return api.get(`/groups?${queryParams}`);
  },
  getMyGroups: () => api.get('/groups/me'),
  createGroup: (data) => api.post('/groups', data),
  getGroup: (slug) => api.get(`/groups/${slug}`),
  joinGroup: (groupId) => api.post(`/groups/${groupId}/join`),
  leaveGroup: (groupId) => api.post(`/groups/${groupId}/leave`),
  getGroupFeed: (groupId, cursor, limit = 20) => {
    const params = new URLSearchParams();
    if (cursor) params.append('cursor', cursor);
    params.append('limit', limit);
    return api.get(`/groups/${groupId}/feed?${params}`);
  },
  createGroupPost: (groupId, data) => api.post(`/groups/${groupId}/posts`, data),
};

// Treatment APIs
export const treatmentAPI = {
  getModules: () => api.get('/tratamento/modules'),
  completeModule: (code) => api.post(`/tratamento/modules/${code}/complete`),
};

// Profile APIs
export const profileAPI = {
  getProfile: () => api.get('/me/profile'),
  updateProfile: (data) => api.put('/me/profile', data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getEntitlements: () => api.get('/me/entitlements'),
};

// Affiliates APIs
export const affiliatesAPI = {
  trackClick: (affCode) => api.post('/affiliates/clicks', { aff_code: affCode }),
};

// Meetings APIs
export const meetingsAPI = {
  getMeetings: (params) => {
    const queryParams = new URLSearchParams();
    if (params?.q) queryParams.append('q', params.q);
    if (params?.topic) queryParams.append('topic', params.topic);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.visibility) queryParams.append('visibility', params.visibility);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.cursor) queryParams.append('cursor', params.cursor);
    if (params?.limit) queryParams.append('limit', params.limit);
    return api.get(`/meetings?${queryParams}`);
  },
  getMyMeetings: (cursor, limit = 20) => {
    const params = new URLSearchParams();
    if (cursor) params.append('cursor', cursor);
    params.append('limit', limit);
    return api.get(`/meetings/mine?${params}`);
  },
  createMeeting: (data) => api.post('/meetings', data),
  getMeeting: (slug) => api.get(`/meetings/${slug}`),
  startMeeting: (meetingId) => api.post(`/meetings/${meetingId}/start`),
  endMeeting: (meetingId) => api.post(`/meetings/${meetingId}/end`),
  joinMeeting: (meetingId) => api.post(`/meetings/${meetingId}/join`),
  reportMeeting: (meetingId, reason) => api.post(`/meetings/${meetingId}/report`, { reason }),
  createInvite: (meetingId, data) => api.post(`/meetings/${meetingId}/invite`, data),
  acceptInvite: (token) => api.post(`/meetings/invite/${token}/accept`),
  getRTCToken: (meetingId) => api.post(`/meetings/${meetingId}/rtc-token`),
  lockMeeting: (meetingId) => api.post(`/meetings/${meetingId}/lock`),
  unlockMeeting: (meetingId) => api.post(`/meetings/${meetingId}/unlock`),
  kickParticipant: (meetingId, userId) => api.post(`/meetings/${meetingId}/kick`, { user_id: userId }),
};

// Courses APIs
export const coursesAPI = {
  getCourse: () => api.get('/courses/parei-ad'),
  getModule: (moduleNumber) => api.get(`/courses/parei-ad/modules/${moduleNumber}`),
  getProgress: () => api.get('/courses/parei-ad/progress'),
  markModuleCompleted: (moduleNumber) => api.post(`/courses/parei-ad/progress/module/${moduleNumber}`),
  submitQuiz: (submission) => api.post('/courses/parei-ad/quiz/submit', submission),
  getQuizAttempts: () => api.get('/courses/parei-ad/quiz/attempts'),
  getCertificate: () => api.get('/courses/parei-ad/certificate'),
  getStats: () => api.get('/courses/parei-ad/stats'),
};

// Shop APIs
export const shopAPI = {
  listProducts: () => api.get('/shop/products'),
  getProduct: (productId) => api.get(`/shop/products/${productId}`),
};

// Tokens APIs
export const tokensAPI = {
  getBalance: () => api.get('/tokens/balance'),
  getAvailableTasks: () => api.get('/tokens/tasks/available'),
  completeTask: (taskType, metadata = {}) => api.post('/tokens/tasks/complete', { task_type: taskType, metadata }),
  getTransactions: (limit = 50) => api.get('/tokens/transactions', { params: { limit } }),
  getStats: () => api.get('/tokens/stats'),
  // Doação manual (mock)
  recordDonation: (amount) => api.post('/tokens/donation', { amount }),
};

// Referrals APIs
export const referralsAPI = {
  getMyLink: () => api.get('/referrals/my-link'),
  getMyStats: () => api.get('/referrals/my-stats'),
  getMyReferrals: () => api.get('/referrals/my-referrals'),
  getRanking: (limit = 50) => api.get(`/referrals/ranking?limit=${limit}`),
  validateCode: (code) => api.post(`/referrals/validate-code/${code}`),
  getMySlug: () => api.get('/referrals/my-slug'),
  customizeSlug: (slug) => api.post('/referrals/customize-slug', { slug }),
};

// Activities APIs (Questionário e Check-in)
export const activitiesAPI = {
  getQuestionnaireStatus: () => api.get('/me/questionnaire'),
  submitQuestionnaire: (data) => api.post('/me/questionnaire', data),
  getCheckinToday: () => api.get('/me/checkin/today'),
  submitCheckin: (data) => api.post('/me/checkin', data),
  getCheckinHistory: (limit = 30) => api.get(`/me/checkin/history?limit=${limit}`),
  completeQuiz: (data) => api.post('/me/quiz/complete', data),
};

// Daily Activities APIs
export const dailyActivitiesAPI = {
  getToday: () => api.get('/daily-activities/today'),
  submit: (answers) => api.post('/daily-activities/submit', { answers }),
  getHistory: (limit = 7) => api.get(`/daily-activities/history?limit=${limit}`),
};

// Course Content APIs
export const courseAPI = {
  // Professional
  getProfessionalModules: () => api.get('/courses/professional/modules'),
  getProfessionalModule: (num) => api.get(`/courses/professional/modules/${num}`),
  submitProfessionalQuiz: (num, answers) => api.post(`/courses/professional/modules/${num}/submit`, { answers }),
  getProfessionalCertificate: () => api.get('/courses/professional/certificate'),
  // Family
  getFamilyDays: () => api.get('/courses/family/days'),
  getFamilyDay: (num) => api.get(`/courses/family/days/${num}`),
  submitFamilyQuiz: (num, answers) => api.post(`/courses/family/days/${num}/submit`, { answers }),
};

// VSA Wallet APIs
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  getTransactions: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.wallet_type) queryParams.append('wallet_type', params.wallet_type);
    return api.get(`/wallet/transactions?${queryParams}`);
  },
  getStats: () => api.get('/wallet/stats'),
  creditWallet: (data) => api.post('/wallet/credit', data),
  createStaking: (amount_awa) => api.post('/wallet/staking/create', { amount_awa }),
  getStakingPositions: () => api.get('/wallet/staking/positions'),
  requestWithdraw: (data) => api.post('/wallet/withdraw/request', data),
  getWithdrawRequests: () => api.get('/wallet/withdraw/requests'),
};

// Premium APIs
export const premiumAPI = {
  getPlans: () => api.get('/premium/plans'),
  createCheckout: (planId, originUrl) => api.post(`/premium/checkout/create?plan_id=${planId}&origin_url=${encodeURIComponent(originUrl)}`),
  getCheckoutStatus: (sessionId) => api.get(`/premium/checkout/status/${sessionId}`),
  getStatus: () => api.get('/premium/status'),
  getAffiliateStats: () => api.get('/premium/affiliates/stats'),
  getAffiliateReferrals: () => api.get('/premium/affiliates/referrals'),
};

// Garden APIs (Jardim da Alma)
export const gardenAPI = {
  // Catálogo
  getPlantsCatalog: (category) => {
    const params = category ? `?category=${category}` : '';
    return api.get(`/garden/plants/catalog${params}`);
  },
  getPlantInfo: (plantId) => api.get(`/garden/plants/${plantId}`),
  
  // Questionário
  getQuestionnaire: () => api.get('/garden/questionnaire'),
  getQuestionnaireStatus: () => api.get('/garden/questionnaire/status'),
  submitQuestionnaire: (answers) => api.post('/garden/questionnaire/submit', { answers }),
  
  // Minhas Plantas
  getMyPlants: () => api.get('/garden/my-plants'),
  plantSeed: (plantId, nameCustom) => api.post('/garden/plant', { plant_id: plantId, name_custom: nameCustom }),
  waterPlant: (plantId) => api.post(`/garden/water/${plantId}`),
  reportRelapse: (plantId) => api.post(`/garden/relapse/${plantId}`),
  
  // Estatísticas e Badges
  getStats: () => api.get('/garden/stats'),
  getBadges: () => api.get('/garden/badges'),
};

// Search API
export const searchAPI = {
  search: (q, limit = 10) => api.get(`/search?q=${encodeURIComponent(q)}&limit=${limit}`),
};

export default api;
